import { gql } from '@apollo/client';

/**
 * Combine a client's orders into one shipment at the END OF PICK and at VERIFY — the shipping
 * bundle the Ship step builds, joined earlier so bundles stop piling up at Ship.
 *
 *  - THE ALERT: `pickingOrders` — the client's other orders still being picked that could share this
 *    order's carton and are not in its shipment yet (the server applies every rule: Picking status
 *    only, same client, same address / class, no controlled product). Empty means no alert.
 *  - MERGE BY SCAN: the other order's pick ticket (or its SO number typed) → `salesOrderCombine`.
 *  - THE SHIPMENT: `shipment` — every order combined with this one, the PARENT (the one that entered
 *    Verify first) first. Verify completes them together; Ship ships them as one bundle.
 *
 * The rules that decide anything live on the server (libs/services shipping-bundle/combine.service);
 * this file only words what it says.
 */

export const COMBINE_ORDER_FIELDS = gql`
  fragment CombineOrderFields on SalesOrderCombineOrder {
    id
    vendorSalesOrderNumber
    stage
    itemCount
    verifiedUnits
    requiredUnits
    verifyComplete
    pickerName
    isParent
    isCurrent
  }
`;

export const COMBINE_CONTEXT_FIELDS = gql`
  ${COMBINE_ORDER_FIELDS}
  fragment CombineContextFields on SalesOrderCombineContext {
    salesOrderId
    vendorSalesOrderNumber
    clientName
    combinable
    reason
    shipment {
      id
      bundleNumber
      status
      parentOrderId
      orders {
        ...CombineOrderFields
      }
    }
    pickingOrders {
      ...CombineOrderFields
    }
  }
`;

export const SALES_ORDER_COMBINE_CONTEXT = gql`
  ${COMBINE_CONTEXT_FIELDS}
  query SalesOrderCombineContext($companyId: ID!, $salesOrderId: ID!) {
    salesOrderCombineContext(companyId: $companyId, salesOrderId: $salesOrderId) {
      ...CombineContextFields
    }
  }
`;

export const SALES_ORDER_COMBINE = gql`
  ${COMBINE_CONTEXT_FIELDS}
  mutation SalesOrderCombine(
    $companyId: ID!
    $salesOrderId: ID!
    $code: String
    $combineSalesOrderId: ID
  ) {
    salesOrderCombine(
      companyId: $companyId
      salesOrderId: $salesOrderId
      code: $code
      combineSalesOrderId: $combineSalesOrderId
    ) {
      combinedOrderId
      combinedOrderNumber
      bundleNumber
      alreadyCombined
      context {
        ...CombineContextFields
      }
    }
  }
`;

/** The Ship step's own remove — the same bundle, so the same way out of it. */
export const COMBINE_REMOVE_ORDER = gql`
  mutation CombineRemoveOrder($companyId: ID!, $salesOrderId: ID!) {
    shippingBundleRemoveOrder(companyId: $companyId, salesOrderId: $salesOrderId) {
      id
    }
  }
`;

export type CombineStage = 'picking' | 'verifying' | 'ready';

export type CombineOrder = {
  id: string;
  vendorSalesOrderNumber?: string | null;
  stage: CombineStage | string;
  itemCount: number;
  verifiedUnits?: number | null;
  requiredUnits?: number | null;
  verifyComplete: boolean;
  pickerName?: string | null;
  isParent: boolean;
  isCurrent: boolean;
};

export type CombineShipment = {
  id: string;
  bundleNumber: string;
  status: string;
  parentOrderId?: string | null;
  orders: CombineOrder[];
};

export type CombineContext = {
  salesOrderId: string;
  vendorSalesOrderNumber?: string | null;
  clientName?: string | null;
  combinable: boolean;
  reason?: string | null;
  shipment?: CombineShipment | null;
  pickingOrders: CombineOrder[];
};

/** Where the combine step is shown: at the end of the pick path, or at Verify. */
export type CombineVariant = 'pick' | 'verify';

export const soLabel = (order: { vendorSalesOrderNumber?: string | null } | null | undefined) =>
  `SO #${order?.vendorSalesOrderNumber || '—'}`;

/** "SO #1", "SO #1 and SO #2", "SO #1, SO #2 and SO #3". */
export const soList = (orders: readonly { vendorSalesOrderNumber?: string | null }[]) => {
  const labels = orders.map(soLabel);
  if (labels.length <= 1) return labels[0] || '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** The orders that raise the alert — none for an order that cannot be combined at all. */
export const combineAlertOrders = (context: CombineContext | null | undefined): CombineOrder[] =>
  context?.combinable ? context.pickingOrders || [] : [];

/**
 * Identity of the alert on screen: the set of orders it is about. The one-time alert sheet opens
 * again only when this changes to include an order the operator has not been told about.
 */
export const combineAlertIds = (context: CombineContext | null | undefined): string[] =>
  combineAlertOrders(context).map(({ id }) => id);

/** The orders combined with this one (the shipment's members other than the current order). */
export const combinePartners = (context: CombineContext | null | undefined): CombineOrder[] =>
  (context?.shipment?.orders || []).filter((order) => !order.isCurrent);

/** The order the others are combined into, or null when this order is in no shipment. */
export const combineParent = (context: CombineContext | null | undefined): CombineOrder | null =>
  (context?.shipment?.orders || []).find((order) => order.isParent) || null;

/** One line about where an order of the shipment stands. */
export const combineStageText = (order: CombineOrder): string => {
  if (order.stage === 'ready') return 'Verified · at Ship';
  if (order.stage === 'verifying') {
    if (order.verifyComplete) return 'Verified · ready to complete';
    const required = order.requiredUnits ?? 0;
    return `Verifying · ${order.verifiedUnits ?? 0} of ${required}`;
  }
  return order.pickerName ? `Being picked by ${order.pickerName}` : 'Waiting in Pick';
};

/** The alert's headline and body, worded for the step it interrupts. */
export const combineAlertCopy = (
  variant: CombineVariant,
  context: CombineContext | null | undefined
): { title: string; text: string } => {
  const orders = combineAlertOrders(context);
  const client = context?.clientName || 'This client';
  const which = soList(orders);
  const isAre = orders.length === 1 ? 'is' : 'are';
  if (variant === 'pick') {
    return {
      title: 'Set this order aside',
      text: `${client} has ${which} still being picked. It ${
        orders.length === 1 ? 'is' : 'will all be'
      } combined with this order and shipped together — keep this tote apart for it.`
    };
  }
  return {
    title: 'Another order is coming',
    text: `${client} placed ${which}, which ${isAre} still being picked. ${
      orders.length === 1 ? 'It' : 'They'
    } will be combined with this order and verified and shipped together.`
  };
};

/** The shipment header's summary: "3 orders · ship as one". */
export const combineShipmentSummary = (shipment: CombineShipment | null | undefined) =>
  shipment ? `${plural(shipment.orders.length, 'order', 'orders')} · verified and shipped as one` : '';

/**
 * What Complete Verify means for this order's combined shipment (server rules, worded here):
 *  - `toVerify`: another order at Verify with units still to scan — the verifier does it next;
 *  - `waitingOnPick`: an order still being picked — Complete Verify waits for it (or remove it);
 *  - `completesCount`: how many orders Complete Verify will move to Ship at once (this one + every
 *    partner at Verify). 1 = an ordinary Complete Verify.
 */
export type CombineVerifyGate = {
  toVerify: CombineOrder | null;
  waitingOnPick: CombineOrder | null;
  completesCount: number;
};

export const combineVerifyGate = (context: CombineContext | null | undefined): CombineVerifyGate => {
  const partners = combinePartners(context);
  return {
    toVerify: partners.find((o) => o.stage === 'verifying' && !o.verifyComplete) || null,
    waitingOnPick: partners.find((o) => o.stage === 'picking') || null,
    completesCount: 1 + partners.filter((o) => o.stage === 'verifying').length
  };
};

/** The Complete Verify button's label for a combined shipment. */
export const combineCompleteLabel = (gate: CombineVerifyGate) =>
  gate.completesCount > 1 ? `Complete Verify · ${gate.completesCount} orders` : 'Complete Verify';

/** Why Complete Verify cannot run yet because of the combined shipment, or null. */
export const combineCompleteBlockedReason = (gate: CombineVerifyGate): string | null => {
  if (gate.waitingOnPick) {
    return `Waiting for ${soLabel(gate.waitingOnPick)} — it ships with this order and is still being picked. Remove it from the shipment to complete without it.`;
  }
  if (gate.toVerify) {
    return `${soLabel(gate.toVerify)} ships with this order — verify it next, then complete Verify for both.`;
  }
  return null;
};

/**
 * A Verify scan refused as "not on this sales order" while the order is combined with others: the
 * unit most likely belongs to one of them (their totes sit side by side). Says where to verify it.
 * Any other refusal, or an order combined with nothing at Verify, is returned unchanged.
 */
export const withCombinedNotOnOrderHint = (
  message: string,
  partners: readonly CombineOrder[]
): string => {
  if (!/is not on this sales order\.?$/.test(message)) return message;
  const atVerify = partners.filter((order) => order.stage === 'verifying');
  if (!atVerify.length) return message;
  return `${message} If it belongs to ${soList(atVerify)} in this shipment, verify it on that order.`;
};

/** Where an order of the shipment opens when its row is tapped. */
export const combineOrderPath = (order: CombineOrder): string => {
  if (order.stage === 'verifying') return `/sales-order-verify/${order.id}`;
  if (order.stage === 'picking') return `/sales-order-summary/${order.id}`;
  return `/sales-order-summary/${order.id}`;
};

/** The success toast after a combine. */
export const combinedToast = (result: {
  combinedOrderNumber?: string | null;
  bundleNumber?: string | null;
  alreadyCombined?: boolean | null;
}): { title: string; text: string } =>
  result.alreadyCombined
    ? {
        title: 'Already combined',
        text: `SO #${result.combinedOrderNumber} is already in this shipment.`
      }
    : {
        title: 'Orders combined',
        text: `SO #${result.combinedOrderNumber} now ships with this order${
          result.bundleNumber ? ` (${result.bundleNumber})` : ''
        }.`
      };

const AIM_IDENTIFIER = /^\][A-Za-z][0-9]/;

/**
 * The text of a scan read as a pick ticket / SO number, or null when the read is a product barcode
 * (a GS1 item scan carries a serial or GTIN — never a pick ticket) or empty.
 */
export const pickTicketCodeFromScan = (
  scan: { serialNumber?: string | null; gtin?: string | null; kind?: string | null; text?: string | null } | null
): string | null => {
  if (!scan) return null;
  if (scan.serialNumber || scan.gtin) return null;
  let text = (scan.text || '').trim();
  if (AIM_IDENTIFIER.test(text)) text = text.slice(3).trim();
  return text || null;
};
